import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2, Upload, ExternalLink, Newspaper } from "lucide-react";
import { toast } from "sonner";
import api, { formatError, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

export default function SellerStore({ sellerId }) {
  const [posts, setPosts] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", body: "", image_url: "" });
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => {
    api.get("/seller/posts").then(({ data }) => setPosts(data)).catch((e) => toast.error(formatError(e)));
  }, []);

  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/seller/upload", fd);
      setF((p) => ({ ...p, image_url: data.url }));
      toast.success(data.optimized ? `Image uploaded & optimized (${Math.round(data.size / 1024)}KB)` : "Image uploaded");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    try {
      await api.post("/seller/posts", f);
      toast.success("Post published on your store");
      setOpen(false);
      setF({ title: "", body: "", image_url: "" });
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/seller/posts/${id}`);
      toast.success("Post removed");
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  return (
    <div data-testid="seller-store">
      <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
        <p className="text-sm text-slate-500 flex items-center gap-2">
          <Newspaper className="w-4 h-4" /> Posts appear on your public store page for customers.
        </p>
        <div className="flex gap-2">
          <Button asChild variant="outline" className="rounded-md">
            <Link to={`/store/${sellerId}`} target="_blank" data-testid="view-my-store">
              <ExternalLink className="w-4 h-4 mr-1.5" /> View my store
            </Link>
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-store-post-btn" className="rounded-md bg-slate-900"><Plus className="w-4 h-4 mr-1.5" /> New post</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Post to your store</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <input data-testid="store-post-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Title (e.g. Festive sale this week!)"
                  className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-blue-500" />
                <textarea data-testid="store-post-body" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} placeholder="Write your announcement…" rows={5}
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500" />
                <div>
                  <label className="inline-flex items-center gap-2 text-xs font-semibold text-blue-600 cursor-pointer">
                    <Upload className="w-3.5 h-3.5" /> {uploading ? "Uploading…" : "Upload image (optional)"}
                    <input data-testid="store-post-image-upload" type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files[0])} />
                  </label>
                  <p className="text-[10px] text-slate-400 mt-1">Large images auto-optimized · up to 10MB</p>
                  {f.image_url && <img src={fileUrl(f.image_url)} alt="" className="mt-2 h-20 rounded-lg object-cover" />}
                </div>
                <Button data-testid="store-post-save" onClick={save} disabled={!f.title} className="rounded-md bg-slate-900">Publish post</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {!posts.length ? (
        <div className="text-center py-16 text-slate-400 text-sm border border-dashed border-slate-200 rounded-xl">
          No posts yet. Share updates, offers and announcements with your customers.
        </div>
      ) : (
        <div className="grid md:grid-cols-3 gap-5">
          {posts.map((p) => (
            <div key={p.id} data-testid={`store-post-${p.id}`} className="border border-slate-100 rounded-xl overflow-hidden">
              {p.image_url && <img src={fileUrl(p.image_url)} alt="" className="w-full h-36 object-cover" />}
              <div className="p-4">
                <p className="font-semibold text-sm">{p.title}</p>
                <p className="text-xs text-slate-500 mt-1 line-clamp-2">{p.body}</p>
                <div className="flex justify-between items-center mt-3">
                  <p className="text-[11px] text-slate-400">{new Date(p.created_at).toLocaleDateString("en-IN")}</p>
                  <button data-testid={`delete-store-post-${p.id}`} onClick={() => remove(p.id)} className="text-slate-400 hover:text-red-500 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
